import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../services/api.js';

const DIFF_LABELS = {
  decouverte: '🐣 Découverte',
  curieux: '🔍 Curieux',
  explorateur: '🧭 Explorateur',
  expert: '⭐ Expert',
};

function CelebrationCanvas() {
  const canvasRef = useRef(null);
  const particles = useRef([]);
  const raf = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const colors = ['#f2c063','#ff7a6b','#19b79e','#51cf66','#ff922b','#cc5de8','#22b8cf','#fff'];
    // Burst from center-bottom
    for (let i = 0; i < 100; i++) {
      const angle = (Math.random() * Math.PI) - Math.PI; // upward
      const speed = 3 + Math.random() * 8;
      particles.current.push({
        x: canvas.width / 2, y: canvas.height * 0.7,
        vx: Math.cos(angle) * speed * (0.5 + Math.random()),
        vy: Math.sin(angle) * speed,
        size: 3 + Math.random() * 5,
        color: colors[Math.floor(Math.random() * colors.length)],
        life: 1, shape: Math.random() > 0.5 ? 'circle' : 'rect',
        rot: Math.random() * 360, rotV: (Math.random() - 0.5) * 15,
      });
    }
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.current = particles.current.filter(p => p.life > 0);
      particles.current.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.vy += 0.12; p.rot += p.rotV; p.life -= 0.006;
        ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.rot * Math.PI / 180);
        ctx.globalAlpha = Math.min(1, p.life * 2);
        ctx.fillStyle = p.color;
        if (p.shape === 'circle') {
          ctx.beginPath(); ctx.arc(0, 0, p.size, 0, Math.PI * 2); ctx.fill();
        } else {
          ctx.fillRect(-p.size, -p.size/2, p.size*2, p.size);
        }
        ctx.restore();
      });
      if (particles.current.length > 0) raf.current = requestAnimationFrame(animate);
    };
    raf.current = requestAnimationFrame(animate);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); particles.current = []; };
  }, []);
  return <canvas ref={canvasRef} style={{ position:'fixed',top:0,left:0,pointerEvents:'none',zIndex:50 }} />;
}

function AnimatedNumber({ value, duration = 1500 }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let start = 0;
    const startTime = Date.now();
    const tick = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setDisplay(Math.round(eased * value));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [value, duration]);
  return <>{display}</>;
}

export default function Summary() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDetails] = useState(true);
  const [connProgress, setConnProgress] = useState(null);
  const [themes, setThemes] = useState([]);

  const totalPoints = location.state?.totalPoints || 0;
  const bestStreak = location.state?.bestStreak || 0;

  useEffect(() => {
    api.getSessionSummary(sessionId)
      .then(s => setSummary(s))
      .catch(() => navigate('/'))
      .finally(() => setLoading(false));
  }, [sessionId, navigate]);

  useEffect(() => {
    Promise.all([api.getConnectionProgress(), api.getThemes()])
      .then(([cp, t]) => { setConnProgress(cp); setThemes(t); })
      .catch(() => {});
  }, []);

  const playTheme = async (themeId) => {
    try {
      const s = await api.startQuiz(themeId);
      navigate(`/quiz/${s.id}`, { state: { session: s } });
    } catch (e) { /* ignore */ }
  };

  if (loading || !summary) {
    return null;
  }

  const pct = summary.score_percent;
  const isPerfect = pct === 100;
  const isGreat = pct >= 80;
  const isGood = pct >= 50;

  const emoji = isPerfect ? '👑' : isGreat ? '🌟' : isGood ? '👍' : '💪';
  const message = isPerfect ? 'PARFAIT !' : isGreat ? 'Extraordinaire !' : isGood ? 'Bien joué !' : 'Continue comme ça !';
  const subtitle = isPerfect ? 'Pas une seule erreur, chapeau !'
    : isGreat ? 'Tu maîtrises vraiment le sujet'
    : isGood ? 'Tu progresses bien, continue'
    : 'Chaque erreur est une leçon apprise';

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      {isGreat && <CelebrationCanvas />}

      <nav className="nav">
        <div className="nav-logo" onClick={() => navigate('/')}>Curiosaurus</div>
      </nav>

      <div className="container summary-page">
        {/* Big score reveal */}
        <motion.div className="summary-hero"
          initial={{ scale: 0.3, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', damping: 14 }}>
          <div className="summary-emoji">{emoji}</div>
          <div className="summary-score">
            <AnimatedNumber value={pct} />%
          </div>
          <div className="summary-message">{message}</div>
          <div className="summary-subtitle">{subtitle}</div>
        </motion.div>

        {/* Stats grid */}
        <AnimatePresence>
          {showDetails && (
            <motion.div className="summary-grid"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}>

              <div className="summary-stat-card">
                <div className="summary-stat-icon">✅</div>
                <div className="summary-stat-value">{summary.correct_answers}/{summary.total_questions}</div>
                <div className="summary-stat-label">Bonnes réponses</div>
              </div>

              <div className="summary-stat-card gold summary-stat-clickable" onClick={() => navigate('/ranking')} role="button" tabIndex={0}>
                <div className="summary-stat-icon">🏆</div>
                <div className="summary-stat-value"><AnimatedNumber value={totalPoints} duration={2000} /> <span style={{ fontSize: '0.6em', color: 'var(--text-muted)' }}>pts</span></div>
                <div className="summary-stat-label">Tes points <span className="summary-stat-arrow">→</span></div>
              </div>

              <div className="summary-stat-card">
                <div className="summary-stat-icon">🔥</div>
                <div className="summary-stat-value">{bestStreak}</div>
                <div className="summary-stat-label">Meilleur combo</div>
              </div>

              <div className="summary-stat-card summary-stat-clickable" onClick={() => navigate('/cards')} role="button" tabIndex={0}>
                <div className="summary-stat-icon">✨</div>
                <div className="summary-stat-value" style={{ color:'var(--gold)' }}>{summary.new_cards_unlocked}</div>
                <div className="summary-stat-label">Nouvelles fiches <span className="summary-stat-arrow">→</span></div>
              </div>

              <div className="summary-stat-card">
                <div className="summary-stat-icon">📊</div>
                <div className="summary-stat-value">{DIFF_LABELS[summary.difficulty]}</div>
                <div className="summary-stat-label">Ton niveau</div>
              </div>

              <div className="summary-stat-card summary-stat-clickable" onClick={() => navigate('/curiosaurus')} role="button" tabIndex={0}>
                <div className="summary-stat-icon">🌳</div>
                <div className="summary-stat-value">{summary.new_cards_unlocked}</div>
                <div className="summary-stat-label">Feuilles ajoutées <span className="summary-stat-arrow">→</span></div>
              </div>

              <div className="summary-stat-card summary-stat-clickable" onClick={() => navigate('/connections')} role="button" tabIndex={0}>
                <div className="summary-stat-icon">🔗</div>
                <div className="summary-stat-value">Voir</div>
                <div className="summary-stat-label">Connexions <span className="summary-stat-arrow">→</span></div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {connProgress && (
          <div className="conn-card" style={{ maxWidth: 520, margin: '0 auto 24px' }}>
            <div className="conn-card-head">
              <span className="conn-card-title">🔗 Connexions découvertes</span>
              <span className="conn-card-count">{connProgress.discovered}<span className="conn-card-total"> / {connProgress.total}</span></span>
            </div>
            <div className="conn-bar"><span style={{ width: `${connProgress.total ? Math.round(connProgress.discovered / connProgress.total * 100) : 0}%` }} /></div>
            {connProgress.reachable_total > 0 && (
              <>
                <div className="conn-nudge">
                  💡 <strong>{connProgress.reachable_total} connexion{connProgress.reachable_total > 1 ? 's' : ''}</strong> à portée — joue :
                </div>
                <div className="conn-chips">
                  {connProgress.reachable_by_theme.slice(0, 4).map(rt => {
                    const th = themes.find(t => t.slug === rt.theme_slug);
                    const locked = th && th.locked && !th.user_unlocked;
                    return (
                      <button key={rt.theme_slug} className="conn-chip"
                        onClick={() => th ? (locked ? navigate('/achievements') : playTheme(th.id)) : null}>
                        <span className="conn-chip-dot" style={{ background: rt.theme_color }} />
                        {rt.theme_name} <span className="conn-chip-n">{rt.count}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* Actions */}
        <motion.div className="summary-actions"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
          <button className="btn btn-primary btn-lg" onClick={() => navigate('/')}>
            🚀 Nouveau quiz
          </button>
          <button className="btn btn-outline" onClick={() => navigate('/curiosaurus')}>
            🌳 Mon Curiosaurus
          </button>
          <button className="btn btn-outline" onClick={() => navigate('/cards')}>
            📚 Mes fiches ({summary.new_cards_unlocked} nouvelles)
          </button>
        </motion.div>
      </div>
    </motion.div>
  );
}
